import { fallback } from "@tanstack/zod-adapter";
import { z } from "zod";

/** Where the creator typically works. */
export const SHOOTING_SETTINGS = [
  "Studio",
  "Outdoor",
  "On-Location",
  "Available Light",
  "Mit Blitz",
  "Reise / weltweit",
] as const;
export type ShootingSetting = (typeof SHOOTING_SETTINGS)[number];

/** Languages a creator speaks/works in. */
export const LANGUAGES = [
  "Deutsch",
  "Englisch",
  "Französisch",
  "Italienisch",
  "Spanisch",
  "Türkisch",
  "Russisch",
  "Polnisch",
] as const;
export type Language = (typeof LANGUAGES)[number];

/**
 * Zod schema for the shared filter search params.
 * All filters are optional; each route picks which ones it exposes.
 */
export const creatorFiltersSchema = z.object({
  country: fallback(z.string(), "").default(""),
  city: fallback(z.string(), "").default(""),
  theme: fallback(z.string(), "").default(""),
  price: fallback(z.string(), "").default(""),
  setting: fallback(z.string(), "").default(""),
  language: fallback(z.string(), "").default(""),
  page: fallback(z.number().int().min(1).max(1000), 1).default(1),
});

export type CreatorFilters = z.infer<typeof creatorFiltersSchema>;

/** Test whether a creator matches the active filter values. */
export function matchesCreatorFilters<
  T extends {
    country?: string | null;
    city?: string | null;
    region?: string | null;
    themes?: string[];
    priceFrom?: number;
    shootingSettings?: string[];
    languages?: string[];
  },
>(
  creator: T,
  filters: Partial<CreatorFilters>,
  priceBuckets: ReadonlyArray<{ id: string; min: number; max: number }>,
): boolean {
  if (filters.country && creator.country !== filters.country) return false;
  if (filters.city) {
    const pc = creator.city ?? creator.region ?? "";
    if (pc !== filters.city) return false;
  }
  if (filters.theme && !(creator.themes ?? []).includes(filters.theme)) return false;
  if (filters.price) {
    const bucket = priceBuckets.find((b) => b.id === filters.price);
    if (bucket) {
      const pf = creator.priceFrom ?? 0;
      if (pf < bucket.min || pf > bucket.max) return false;
    }
  }
  if (filters.setting) {
    if (!(creator.shootingSettings ?? []).includes(filters.setting)) return false;
  }
  if (filters.language) {
    if (!(creator.languages ?? []).includes(filters.language)) return false;
  }
  return true;
}

/** True if any filter (other than page) is active. */
export function hasActiveFilters(f: Partial<CreatorFilters>): boolean {
  return Boolean(f.country || f.city || f.theme || f.price || f.setting || f.language);
}